import {
  ChatHeader,
  ChatMessageInputBar,
  ChatPageSkeletonLayout,
  ContactInfoCard,
  DiscussionLayout,
  Icon,
  useIfUser,
} from '@if/mui'
import type { ChatMessageInputBarHandle, MessageVariant } from '@if/mui'
import { ECreditApplicationStatus, ELanguage, EMessageType } from '@if/core/types'
import { IconButton } from '@mui/material'
import { SquareArrowOutUpRight } from 'lucide-react'
import { Constants } from '@src/types/Constants'
import { useTranslation } from 'react-i18next'
import {
  useDeleteMessage,
  useEditMessage,
  useGetConversation,
  useMarkConversationAsRead,
  useMarkMessageRead,
  useMarkMessageUnread,
  useSendMessage,
} from '@src/data/api/chat/chat-api'
import type { ChatMessage } from '@if/core/types'
import {
  applyChatMessageEvent,
  getSignalRConnection,
  useSignalRConnectionReady,
  type MessageUpdatedEvent,
  MessageUpdateType,
} from '@src/hooks/useChatMessageSignalR'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

const CURRENT_USER_PARTICIPANT_TYPE = EMessageType.External

const ChatPage = () => {
  const { creditApplicationId, financingProgramId } = useParams<{
    creditApplicationId: string
    financingProgramId: string
  }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const user = useIfUser()
  const { t } = useTranslation()
  const [formatMode, setFormatMode] = useState(true)

  const getMessageVariant = useCallback(
    (message: ChatMessage): MessageVariant => {
      if (message.sentById === Constants.SystemUserId) return 'neutral'
      if (message.sentById === user?.id) return 'sent'
      return 'received'
    },
    [user?.id],
  )

  const messageInputRef = useRef<ChatMessageInputBarHandle>(null)

  // Data fetching
  const [conversation, isLoading] = useGetConversation(creditApplicationId, financingProgramId)

  const isCancelled =
    conversation?.applicationStatus === ECreditApplicationStatus.Cancelled ||
    conversation?.applicationStatus === ECreditApplicationStatus.Expired

  // Message mutations
  const [sendMsg] = useSendMessage()
  const [deleteMsg] = useDeleteMessage()
  const [editMsg] = useEditMessage()
  const [markUnread] = useMarkMessageUnread()
  const [markAsRead] = useMarkMessageRead()
  const [markConversationAsRead] = useMarkConversationAsRead()

  const [localMessages, setLocalMessages] = useState<ChatMessage[]>([])

  // SignalR real-time message updates
  const signalRReady = useSignalRConnectionReady()
  const markAsReadRef = useRef(markAsRead)
  markAsReadRef.current = markAsRead

  useEffect(() => {
    const connection = getSignalRConnection()
    if (!connection) return

    const handleMessageUpdated = (event: MessageUpdatedEvent) => {
      if (event.creditApplicationId !== creditApplicationId) return

      setLocalMessages((prev) => applyChatMessageEvent(prev, event))

      if (event.updateType === MessageUpdateType.Created && event.updatedById && event.updatedById !== user?.id) {
        void markAsReadRef.current({ creditApplicationId: creditApplicationId ?? '', messageId: event.id })
      }
    }

    connection.on('messageUpdated', handleMessageUpdated)

    return () => {
      connection.off('messageUpdated', handleMessageUpdated)
    }
  }, [creditApplicationId, user?.id, signalRReady])

  // Initialize local messages from server data
  useEffect(() => {
    if (conversation?.messages) {
      setLocalMessages(conversation.messages)

      if (creditApplicationId && conversation.messages.some((m) => !m.isRead && m.sentById !== user?.id)) {
        void markConversationAsRead({ creditApplicationId }).then((response) => {
          if (response?.messageCount > 0) {
            setLocalMessages((prev) =>
              prev.map((m) =>
                !m.isRead && m.sentById !== user?.id
                  ? { ...m, isRead: true, readAt: new Date(), readByName: user?.fullName }
                  : m,
              ),
            )
          }
        })
      }
    }
  }, [conversation?.messages, creditApplicationId, user?.id, user?.fullName, markConversationAsRead, setLocalMessages])

  const handleDeleteMessage = useCallback(
    async (messageId: string) => {
      if (isCancelled || !creditApplicationId) return
      await deleteMsg({ creditApplicationId, messageId })
      setLocalMessages((prev) => prev.filter((m) => m.id !== messageId))
    },
    [creditApplicationId, deleteMsg, isCancelled, setLocalMessages],
  )

  const handleEditMessage = useCallback(
    async (messageId: string, newContent: string) => {
      if (isCancelled || !creditApplicationId) return
      await editMsg({ creditApplicationId, messageId, content: newContent })
      setLocalMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, content: newContent, editedAt: new Date() } : m)),
      )
    },
    [creditApplicationId, editMsg, isCancelled, setLocalMessages],
  )

  const handleMarkUnreadMessage = useCallback(
    async (messageId: string) => {
      if (isCancelled || !creditApplicationId) return
      await markUnread({ creditApplicationId, messageId })
      setLocalMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, isRead: false, readAt: undefined, markedUnreadById: user?.id } : m,
        ),
      )
    },
    [creditApplicationId, markUnread, user?.id, isCancelled, setLocalMessages],
  )

  const handleSendMessage = useCallback(
    async (messageContent: string) => {
      if (!creditApplicationId || !financingProgramId || !user) return
      await sendMsg({
        id: crypto.randomUUID(),
        content: messageContent,
        sentAt: new Date(),
        sentByName: user.fullName,
        sentById: user.id,
        merchantId: user.merchantId,
        creditApplicationId,
        financingProgramId,
        isRead: false,
        type: CURRENT_USER_PARTICIPANT_TYPE,
      })
    },
    [creditApplicationId, financingProgramId, user, sendMsg],
  )

  const openedFrom = searchParams.get('from')

  const handleApplicationClick = useCallback(() => {
    if (financingProgramId && creditApplicationId) {
      navigate(`/credits/${financingProgramId}/${creditApplicationId}`)
    }
  }, [navigate, financingProgramId, creditApplicationId])

  const handleBack = useCallback(() => {
    if (openedFrom === 'application') {
      handleApplicationClick()
    } else {
      navigate('/chat-dashboard')
    }
  }, [openedFrom, handleApplicationClick, navigate])

  const language = useMemo(() => {
    const lang = conversation?.merchantLanguage
    return lang === ELanguage.French ? 'FR' : 'EN'
  }, [conversation?.merchantLanguage])

  if (isLoading) {
    return <ChatPageSkeletonLayout maxWidth={false} />
  }

  return (
    <DiscussionLayout
      maxWidth={false}
      messages={localMessages}
      systemMessage={conversation?.systemMessage}
      currentUserId={user?.id ?? ''}
      isAgent={false}
      onDeleteMessage={handleDeleteMessage}
      onEditMessage={handleEditMessage}
      onMarkUnreadMessage={handleMarkUnreadMessage}
      isLockedByOther={false}
      isCancelled={isCancelled}
      getMessageVariant={getMessageVariant}
      header={
        <ChatHeader
          province={conversation?.province}
          language={language}
          isAgent={false}
          currentUserId={user?.id ?? ''}
          onBack={handleBack}
          backLabel={openedFrom === 'application' ? t('instantMessage.backToApplication') : undefined}
        />
      }
      subheader={
        <ContactInfoCard
          referenceNumber={conversation?.conversationNumber ?? ''}
          fullName={conversation?.fullName ?? ''}
          email={conversation?.email ?? ''}
          phone={conversation?.phone ?? ''}
          merchantName={conversation?.merchantName ?? ''}
          createdByName={conversation?.applicationCreatedByName ?? ''}
          applicationStatus={conversation?.applicationStatus as ECreditApplicationStatus | undefined}
          onApplicationClick={handleApplicationClick}
          applicationHint={
            <IconButton
              href={`/credits/${financingProgramId}/${creditApplicationId}`}
              size="small"
              color="primary"
              sx={{ p: 0, ml: '-4px !important' }}
            >
              <Icon icon={SquareArrowOutUpRight} size="sm" />
            </IconButton>
          }
        />
      }
      footer={
        <ChatMessageInputBar
          ref={messageInputRef}
          onSendMessage={handleSendMessage}
          formatMode={formatMode}
          onFormatModeChange={setFormatMode}
          disabled={isCancelled}
          showTemplateHint={false}
        />
      }
    />
  )
}

export default memo(ChatPage)
